import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import ErrorBoundary from './components/Common/ErrorBoundary';

// Import CSS
import './styles/theme.css';

// Lazy-loaded pages (code splitting — each page loads on demand)
const Login               = React.lazy(() => import('./pages/Login'));
const ScanUpload          = React.lazy(() => import('./pages/ScanUpload'));
const ScanDoc             = React.lazy(() => import('./pages/ScanDoc'));
const Dashboard           = React.lazy(() => import('./pages/Dashboard'));
const Appointments        = React.lazy(() => import('./pages/Appointments'));
const Leads               = React.lazy(() => import('./pages/Leads'));
const Patients            = React.lazy(() => import('./pages/Patients'));
const PIDManagement       = React.lazy(() => import('./pages/PIDManagement'));
const StudyManagement     = React.lazy(() => import('./pages/StudyManagement'));
const MasterDataSettings  = React.lazy(() => import('./pages/MasterDataSettings'));
const Reports             = React.lazy(() => import('./pages/Reports'));
const Centers             = React.lazy(() => import('./pages/Centers'));
const CenterContracts     = React.lazy(() => import('./pages/CenterContracts'));
const Billing             = React.lazy(() => import('./pages/Billing'));
const Scanners            = React.lazy(() => import('./pages/Scanners'));
const HR                  = React.lazy(() => import('./pages/HR'));
const EmployeeAttendance  = React.lazy(() => import('./pages/EmployeeAttendance'));
const Payroll             = React.lazy(() => import('./pages/Payroll'));
const Recruitment         = React.lazy(() => import('./pages/Recruitment'));
const CompanyDocuments    = React.lazy(() => import('./pages/CompanyDocuments'));
const Assets              = React.lazy(() => import('./pages/Assets'));
const AssetMaintenance    = React.lazy(() => import('./pages/AssetMaintenance'));
const EnvironmentMonitoring = React.lazy(() => import('./pages/EnvironmentMonitoring'));
const DailyCashClosing    = React.lazy(() => import('./pages/DailyCashClosing'));
const Procurement         = React.lazy(() => import('./pages/Procurement'));
const AIMaterialManagement = React.lazy(() => import('./pages/AIMaterialManagement/AIMaterialManagementPage'));
const AIToolsSettings      = React.lazy(() => import('./pages/AIMaterialManagement/AIToolsSettingsPage'));
const Stock               = React.lazy(() => import('./pages/Stock'));
const PettyCash           = React.lazy(() => import('./pages/PettyCash'));
const PettyCashAP         = React.lazy(() => import('./pages/PettyCashAP'));
const StudyReporting      = React.lazy(() => import('./pages/StudyReporting'));
const Finance             = React.lazy(() => import('./pages/Finance'));
const Equity              = React.lazy(() => import('./pages/Equity'));
const ItemMaster          = React.lazy(() => import('./pages/ItemMaster'));
const SimpleSettingsPage  = React.lazy(() => import('./pages/Settings/SimpleSettingsPage'));
const UserManagement      = React.lazy(() => import('./pages/Settings/UserManagement'));
const RoleManagement      = React.lazy(() => import('./pages/Settings/RoleManagement'));
const MWLGatewaySettings  = React.lazy(() => import('./pages/Settings/MWLGatewaySettings'));
const RemoteConsole2FA   = React.lazy(() => import('./pages/Settings/RemoteConsole2FA'));
const RemoteAccess        = React.lazy(() => import('./pages/RemoteAccess'));
const AuditLog            = React.lazy(() => import('./pages/Settings/AuditLog'));
const BackupPage          = React.lazy(() => import('./pages/Settings/BackupPage'));
const PatientHistory      = React.lazy(() => import('./pages/PatientHistory'));
const ReferringPhysicians = React.lazy(() => import('./pages/ReferringPhysicians'));
const PartnerDashboard    = React.lazy(() => import('./pages/PartnerDashboard'));
const PartnerPatients     = React.lazy(() => import('./pages/PartnerPatients'));
import Layout from './components/Layout';

// Spinner shown while a lazy page chunk is loading
const PageSpinner = () => (
  <div className="flex h-screen items-center justify-center bg-slate-50">
    <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
  </div>
);

// Where a user lands by default. Revenue-share partners (PARTNER_VIEW only, no full
// dashboard) land on their own dashboard; everyone else on the main dashboard.
const userPerms = () => { try { return JSON.parse(localStorage.getItem('user'))?.permissions || []; } catch { return []; } };
const landingPath = (perms = userPerms()) =>
  (!perms.includes('DASHBOARD_VIEW') && perms.includes('PARTNER_VIEW')) ? '/partner-dashboard' : '/dashboard';

// Permission-gated route — redirects to the user's landing page if they lack the required permission
// permission can be a string or an array of strings (any match = allowed)
const ProtectedRoute = ({ element, permission }) => {
  const user = (() => { try { return JSON.parse(localStorage.getItem('user')) || {}; } catch { return {}; } })();
  const perms = Array.isArray(user.permissions) ? user.permissions : [];
  const required = Array.isArray(permission) ? permission : (permission ? [permission] : []);
  const aliases = { PETTY_CASH_WRITE: ['PETTY_CASH_CREATE'] };
  const allowed = !required.length
    || perms.includes('ALL_ACCESS')
    || required.some(p => perms.includes(p) || (aliases[p] || []).some(alias => perms.includes(alias)));
  return allowed ? element : <Navigate to={landingPath(perms)} replace />;
};

// Create QueryClient
const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false },
    mutations: { retry: 1 },
  },
});

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = (() => { try { return JSON.parse(localStorage.getItem('user')); } catch { return null; } })();
    const valid = !!(token && user && Array.isArray(user.permissions));
    setIsAuthenticated(valid);
  }, []);

  // Global 401 interceptor — auto-redirect to login on expired/invalid session
  useEffect(() => {
    const origFetch = window.fetch;
    window.fetch = async (...args) => {
      const res = await origFetch(...args);
      if (res.status === 401 && isAuthenticated) {
        const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
        // Only intercept our own API calls, not external
        if (url.startsWith('/api/') || url.startsWith(window.location.origin + '/api/')) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          // No redirect: flipping isAuthenticated renders the Login form AT THE
          // CURRENT PATH, and Login's deep-link return sends the user straight
          // back here after re-auth. A hard '/login' redirect was destroying
          // in-progress work (a cashier lost a registered patient's bill cart
          // mid-billing when an 8h token expired at the Generate Bill click).
          try { sessionStorage.setItem('sessionExpired', '1'); } catch { /* private mode */ }
          setIsAuthenticated(false);
        }
      }
      return res;
    };
    return () => { window.fetch = origFetch; };
  }, [isAuthenticated]);

  return (
    <QueryClientProvider client={queryClient}>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <div className="App">
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: { fontSize: '14px' },
              success: { iconTheme: { primary: '#0d9488', secondary: '#fff' } },
            }}
          />
          <ErrorBoundary>
            <Suspense fallback={<PageSpinner />}>
              <Routes>
              <Route path="/login" element={<Login />} />
              {/* public: QR-handoff phone upload — the tokenized URL is the credential.
                  The tokenless route exists because the page scrubs the token from the
                  address bar; a reload then lands here and must show the friendly
                  "link no longer valid" state instead of a blank page. */}
              <Route path="/scan-upload/:token" element={<ScanUpload />} />
              <Route path="/scan-upload" element={<ScanUpload />} />
              {/* public: printed consent forms' QR lands here — displays only what
                  the QR itself carries (same data printed on the paper) */}
              <Route path="/scan-doc" element={<ScanDoc />} />
              <Route
                path="/"
                element={isAuthenticated ? <Layout /> : <Login />}
              >
                <Route index element={<Navigate to={landingPath()} replace />} />
                <Route path="dashboard"        element={<ProtectedRoute permission="DASHBOARD_VIEW"        element={<Dashboard />} />} />
                <Route path="partner-dashboard" element={<ProtectedRoute permission="PARTNER_VIEW"          element={<PartnerDashboard />} />} />
                <Route path="partner-patients"  element={<ProtectedRoute permission="PARTNER_VIEW"          element={<PartnerPatients />} />} />
                <Route path="appointments"      element={<ProtectedRoute permission="APPOINTMENT_VIEW"      element={<Appointments />} />} />
                <Route path="leads"             element={<ProtectedRoute permission={['LEAD_VIEW', 'LEAD_MANAGE']} element={<Leads />} />} />
                <Route path="patients"         element={<ProtectedRoute permission="PATIENT_VIEW"          element={<Patients />} />} />
                <Route path="patient-history"  element={<ProtectedRoute permission="PATIENT_VIEW"          element={<PatientHistory />} />} />
                <Route path="pid-management"   element={<ProtectedRoute permission="PATIENT_VIEW"          element={<PIDManagement />} />} />
                <Route path="study-management" element={<ProtectedRoute permission="STUDY_VIEW"            element={<StudyManagement />} />} />
                <Route path="study-reporting"       element={<ProtectedRoute permission="STUDY_VIEW"       element={<StudyReporting />} />} />
                <Route path="referring-physicians" element={<ProtectedRoute permission="PHYSICIAN_VIEW"    element={<ReferringPhysicians />} />} />
                {/* VENDOR_WRITE admits payables-only users: Finance.jsx renders just the
                    Payables group for them (payablesOnly), backend enforces per-API perms. */}
                <Route path="finance"          element={<ProtectedRoute permission={['JE_VIEW','VENDOR_WRITE']} element={<Finance />} />} />
                <Route path="equity"           element={<ProtectedRoute permission="EQUITY_VIEW"           element={<Equity />} />} />
                <Route path="petty-cash"       element={<ProtectedRoute permission="PETTY_CASH_VIEW"       element={<PettyCash />} />} />
                <Route path="petty-cash-ap"    element={<ProtectedRoute permission="PETTY_CASH_APPROVE"    element={<PettyCashAP />} />} />
                <Route path="daily-cash-closing" element={<ProtectedRoute permission="CASH_CLOSING_VIEW"    element={<DailyCashClosing />} />} />
                <Route path="employee-attendance" element={<ProtectedRoute element={<EmployeeAttendance />} />} />
                <Route path="hr"               element={<ProtectedRoute permission={['HR_DASHBOARD_VIEW','EMPLOYEE_VIEW','ATTENDANCE_VIEW','LEAVE_APPLY','LEAVE_APPROVE','PAYROLL_VIEW']} element={<HR />} />} />
                <Route path="payroll"          element={<ProtectedRoute permission="PAYROLL_VIEW"          element={<Payroll />} />} />
                <Route path="recruitment"      element={<ProtectedRoute permission={['RECRUITMENT_VIEW','RECRUITMENT_WRITE','OFFER_APPROVE']} element={<Recruitment />} />} />
                <Route path="company-documents" element={<ProtectedRoute permission={['COMPANY_DOC_VIEW','COMPANY_DOC_WRITE']} element={<CompanyDocuments />} />} />
                <Route path="assets"           element={<ProtectedRoute permission="ASSET_VIEW"            element={<Assets />} />} />
                <Route path="asset-maintenance"element={<ProtectedRoute permission="ASSET_MAINTENANCE_VIEW"element={<AssetMaintenance />} />} />
                <Route path="environment-monitoring" element={<ProtectedRoute permission="IOT_MONITOR_VIEW" element={<EnvironmentMonitoring />} />} />
                <Route path="procurement"      element={<ProtectedRoute permission={['PR_VIEW','PO_VIEW','GRN_VIEW']} element={<Procurement />} />} />
                <Route path="ai-material-management" element={<ProtectedRoute permission="AI_SCAN_USE"      element={<AIMaterialManagement />} />} />
                <Route path="ai-tools-settings"      element={<ProtectedRoute permission="AI_SCAN_CONFIG" element={<AIToolsSettings />} />} />
                <Route path="stock"            element={<ProtectedRoute permission="INVENTORY_VIEW"        element={<Stock />} />} />
                <Route path="item-master"      element={<ProtectedRoute permission="INVENTORY_VIEW"        element={<ItemMaster />} />} />
                <Route path="reports"          element={<ProtectedRoute permission={['REPORTS_VIEW','PARTNER_VIEW']} element={<Reports />} />} />
                <Route path="master-data"      element={<ProtectedRoute permission={['MASTER_DATA_VIEW','RAD_REPORTING_MASTER_VIEW']} element={<MasterDataSettings />} />} />
                <Route path="settings"         element={<ProtectedRoute permission="MASTER_DATA_VIEW"      element={<SimpleSettingsPage />} />} />
                <Route path="settings/users"   element={<ProtectedRoute permission={['USER_VIEW','USER_WRITE']} element={<UserManagement />} />} />
                <Route path="settings/roles"   element={<ProtectedRoute permission={['USER_VIEW','USER_WRITE']} element={<RoleManagement />} />} />
                <Route path="settings/mwl"     element={<ProtectedRoute permission="MWL_VIEW"              element={<MWLGatewaySettings />} />} />
                <Route path="settings/remote-2fa" element={<ProtectedRoute permission="ALL_ACCESS"          element={<RemoteConsole2FA />} />} />
                <Route path="remote-access"       element={<ProtectedRoute element={<RemoteAccess />} />} />{/* any signed-in user; the page itself shows only granted consoles */}
                <Route path="settings/audit"   element={<ProtectedRoute permission={['USER_VIEW','USER_WRITE']} element={<AuditLog />} />} />
                <Route path="settings/backup" element={<ProtectedRoute permission="MASTER_DATA_VIEW"           element={<BackupPage />} />} />
                <Route path="centers"          element={<ProtectedRoute permission="CENTER_VIEW"           element={<Centers />} />} />
                <Route path="center-contracts" element={<ProtectedRoute permission="CENTER_CONTRACT_VIEW"  element={<CenterContracts />} />} />
                {/* /customers was static scaffold over a stub API — removed 2026-08-28 with /invoices */}
                <Route path="customers"        element={<Navigate to="/patients" replace />} />
                <Route path="billing"          element={<ProtectedRoute permission="BILLING_VIEW"          element={<Billing />} />} />
                {/* /invoices was a scaffold page over a stub API showing demo data — removed 2026-08-28; real billing lives in Patient History */}
                <Route path="invoices"         element={<Navigate to="/patient-history" replace />} />
                <Route path="scanners"         element={<ProtectedRoute permission="MWL_VIEW"              element={<Scanners />} />} />
              </Route>
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </div>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
